import postalCodes from "postal-codes-js";

import { Button, Checkbox, Divider, Form, Input, Spin, message } from "antd";
import { RuleObject } from "antd/es/form";
import React, { FC, useEffect, useState } from "react";

import styles from "@/components/Account/Address/AddressEditForm/AddressEditForm.module.css";
import { MYKAD_FIELDS, SINGPASS_FIELDS } from "@/constant";
import { updateCustomer } from "@/services/Auth/AuthApi";
import { GetCustomerData } from "@/types/authService";

interface AddressEditFormProps {
  onClose?: () => void;
  editData: GetCustomerData;
}

const CustomRow: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.row}>{children}</div>
);

const AddressEditForm: FC<AddressEditFormProps> = ({ onClose, editData }) => {
  const [form] = Form.useForm();
  const permanentAddressLine1 = Form.useWatch("address_line_1", form);
  const permanentAddressLine2 = Form.useWatch("address_line_2", form);
  const permanentAddressLine3 = Form.useWatch("address_line_3", form);
  const permanentPostalCode = Form.useWatch("postal_code", form);

  const [loading, setLoading] = useState(false);
  const [sameAddress, setSameAddress] = useState(false);

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        address_line_1: editData.address_line_1,
        address_line_2: editData.address_line_2,
        address_line_3: editData.address_line_3,
        postal_code: editData.postal_code,

        corresponding_address_line_1: editData.corresponding_address_line_1,
        corresponding_address_line_2: editData.corresponding_address_line_2,
        corresponding_address_line_3: editData.corresponding_address_line_3,
        corresponding_postal_code: editData.corresponding_postal_code,
      });
    }
  }, [editData, form]);

  const handleSubmit = async (value: AddressEditFormProps): Promise<void> => {
    setLoading(true);
    try {
      const payload = {
        ...value,
      };
      await updateCustomer(String(editData.id), payload);
      message.success(`Addresses updated successfully!`);

      onClose?.();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyOrClearCorresponding = (checked: boolean) => {
    if (checked) {
      const currentAddress = form.getFieldsValue([
        "address_line_1",
        "address_line_2",
        "address_line_3",
        "postal_code",
      ]);
      form.setFieldsValue({
        corresponding_address_line_1: currentAddress.address_line_1,
        corresponding_address_line_2: currentAddress.address_line_2,
        corresponding_address_line_3: currentAddress.address_line_3,
        corresponding_postal_code: currentAddress.postal_code,
      });
    }
    setSameAddress(checked);
  };

  useEffect(() => {
    if (sameAddress) {
      form.setFieldsValue({
        corresponding_address_line_1: permanentAddressLine1,
        corresponding_address_line_2: permanentAddressLine2,
        corresponding_address_line_3: permanentAddressLine3,
        corresponding_postal_code: permanentPostalCode,
      });
    }
  }, [
    sameAddress,
    permanentAddressLine1,
    permanentAddressLine2,
    permanentAddressLine3,
    permanentPostalCode,
  ]);

  const validatePostalCode = (_: RuleObject, value: string): Promise<void> => {
    const country = editData.country;

    if (!value) return Promise.resolve();

    if (country === "Singapore") {
      const result = postalCodes.validate("SG", value);
      return result === true ? Promise.resolve() : Promise.reject("Invalid Singapore postal code");
    }

    if (country === "Malaysia") {
      const result = postalCodes.validate("MY", value);
      return result === true ? Promise.resolve() : Promise.reject("Invalid Malaysia postal code");
    }

    return Promise.resolve();
  };

  const isFieldDisabled = (name: string): boolean => {
    const type = editData?.onboarding_type;
    if (type === "mykad") return MYKAD_FIELDS.includes(name);
    if (type === "singpass") return SINGPASS_FIELDS.includes(name);
    return false;
  };

  return (
    <div className={styles.mainContainer}>
      {loading ? (
        <Spin fullscreen />
      ) : (
        <div className={styles.personalInfoSection}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
            <Divider className={styles.headingText} orientation="left" orientationMargin="0">
              <span className={styles.dividerText}>Permanent Address</span>
            </Divider>
            <CustomRow>
              <Form.Item
                label="Address Line 1"
                name="address_line_1"
                className={styles.formItem}
                rules={[{ required: true, message: "Address Line1 required" }]}
              >
                <Input
                  placeholder="Enter Address Line 1"
                  disabled={isFieldDisabled("address_line_1")}
                />
              </Form.Item>
              <Form.Item label="Address Line 2" name="address_line_2" className={styles.formItem}>
                <Input
                  placeholder="Enter Address Line 2"
                  disabled={isFieldDisabled("address_line_2")}
                />
              </Form.Item>
            </CustomRow>
            <CustomRow>
              <Form.Item label="Address Line 3" name="address_line_3" className={styles.formItem}>
                <Input
                  placeholder="Enter Address Line 3"
                  disabled={isFieldDisabled("address_line_3")}
                />
              </Form.Item>
              <Form.Item
                label="Postal Code"
                name="postal_code"
                className={styles.formItem}
                rules={[
                  { required: true, message: "Postal code is required" },
                  { validator: validatePostalCode },
                ]}
              >
                <Input placeholder="Enter Postal Code" disabled={isFieldDisabled("postal_code")} />
              </Form.Item>
            </CustomRow>

            <Divider className={styles.headingText} orientation="left" orientationMargin="0">
              <span className={styles.dividerText}>Corresponding Address</span>
            </Divider>
            <div className="mb-2">
              <Checkbox
                onChange={(e) => copyOrClearCorresponding(e.target.checked)}
                className={styles.checkboxGroup}
                checked={sameAddress}
              >
                Same as above
              </Checkbox>
            </div>

            <CustomRow>
              <Form.Item
                label="Address Line 1"
                name="corresponding_address_line_1"
                className={styles.formItem}
                rules={[{ required: true, message: "Address Line1 required" }]}
              >
                <Input placeholder="Enter Address Line 1" disabled={sameAddress} />
              </Form.Item>
              <Form.Item
                label="Address Line 2"
                name="corresponding_address_line_2"
                className={styles.formItem}
              >
                <Input placeholder="Enter Address Line 2" disabled={sameAddress} />
              </Form.Item>
            </CustomRow>
            <CustomRow>
              <Form.Item
                label="Address Line 3"
                name="corresponding_address_line_3"
                className={styles.formItem}
              >
                <Input placeholder="Enter Address Line 3" disabled={sameAddress} />
              </Form.Item>
              <Form.Item
                label="Postal Code"
                name="corresponding_postal_code"
                className={styles.formItem}
                rules={[
                  { required: true, message: "Postal code is required" },
                  { validator: validatePostalCode },
                ]}
              >
                <Input placeholder="Enter Postal Code" disabled={sameAddress} />
              </Form.Item>
            </CustomRow>

            <div className={styles.buttonsWrapper}>
              <Button className={styles.clearButton} onClick={() => onClose?.()}>
                Cancel
              </Button>

              <Button htmlType="submit" className={styles.applyButton}>
                Update
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default AddressEditForm;
